// src/api/http.ts
import axios, { AxiosError, AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { authTokenStore } from '../stores/authToken.store.ts';
import { userStore } from '../stores/user.store.ts';
import { useServiceStore } from '../stores/service.store.ts';
import { useBookmarkStore } from '../stores/bookmark.store.ts';
import { ENDPOINTS } from './endpoints.ts';
import { maskSensitive } from './maskSensitive.ts';

type RetryConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshingPromise: Promise<string> | null = null;
let sessionInvalidated = false;

export const SESSION_EXPIRED_FLAG = 'korehalal_session_expired';

/**
 * 세션 정리 후 «지금 페이지» 를 비회원으로 다시 연다. 예전엔 /home 으로 보내서, 오랜만에
 * 공유 링크(게시글 등)로 들어온 사용자가 링크 대신 홈에 떨어졌다. 페이지들은 비회원 상태를
 * 스스로 처리한다(로그인 유도 등). 안내는 리로드에 지워지므로 sessionStorage 로 넘긴다 —
 * 예전엔 replace 직전에 띄운 스낵바가 리로드로 사라져 한 번도 보이지 않았다.
 */
function clearSessionAndReload() {
  if (sessionInvalidated) return;
  sessionInvalidated = true;

  authTokenStore.getState().clearTokens();
  userStore.getState().clearUser();
  useServiceStore.getState().clear();
  useBookmarkStore.getState().clear();

  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch {}

  window.location.reload();
}

const AUTH_PATHS = [
  ENDPOINTS.auth.redirectSocialLogin('google'),
  ENDPOINTS.auth.redirectSocialLogin('kakao'),
  ENDPOINTS.auth.redirectSocialLogin('apple'),
  ENDPOINTS.auth.signIn,
  ENDPOINTS.auth.refreshToken,
] as const;

function isAuthPath(url?: string) {
  if (!url) return false;
  const p = url.toLowerCase();
  return AUTH_PATHS.some((path) => p.includes(path.toLowerCase()));
}

/**
 * refresh_token은 HttpOnly 쿠키로 자동 전송됩니다 (withCredentials: true).
 * 요청 body에 refresh_token을 담지 않습니다.
 */
async function refreshOnce(client: AxiosInstance): Promise<string> {
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = (async () => {
    const res = await client.post(
      ENDPOINTS.auth.refreshToken,
      {},
      { headers: { 'X-Refresh-Request': '1' } }
    );

    const { access_token } = res.data;
    authTokenStore.getState().setAccessToken(access_token);
    return access_token as string;
  })().finally(() => {
    refreshingPromise = null;
  });

  return refreshingPromise;
}

const baseURL = process.env.REACT_APP_API_BASE_URL;

if (!baseURL) {
  throw new Error('Missing env: REACT_APP_API_BASE_URL');
}

export const http = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials: true, // HttpOnly 쿠키(refresh_token) 자동 전송을 위해 필수
});

http.defaults.headers.common['X-Client-Type'] = 'web';

/** 요청·응답 로깅 (개발 환경에서만, 민감 값 마스킹). imageHttp 도 같은 로깅을 쓴다. */
export function attachDevLogging(instance: AxiosInstance, tag: string) {
  instance.interceptors.request.use((config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${tag}] REQUEST`, {
        method: config.method,
        url: config.baseURL ? `${config.baseURL}${config.url}` : config.url,
        data: maskSensitive(config.data),
        params: maskSensitive(config.params),
      });
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${tag}] RESPONSE`, {
          url: res.config?.url,
          status: res.status,
          data: maskSensitive(res.data),
        });
      }
      return res;
    },
    (err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[${tag}] ERROR`, {
          url: err.config?.url,
          status: err.response?.status,
          data: maskSensitive(err.response?.data),
          message: err.message,
        });
      }
      return Promise.reject(err);
    }
  );
}

attachDevLogging(http, 'HTTP');

// Access Token 주입
http.interceptors.request.use((config) => {
  const access = authTokenStore.getState().accessToken;
  if (access) {
    if (!config.headers) config.headers = new AxiosHeaders();
    config.headers.set('Authorization', `Bearer ${access}`);
  }
  return config;
});

// 401 감지 → 쿠키 기반 토큰 갱신 후 원래 요청 재시도
http.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const status = err.response?.status;
    const config = err.config as RetryConfig | undefined;

    if (!config || status !== 401) throw err;
    if (config._retried) throw err;
    if (isAuthPath(config.url)) throw err;
    if (sessionInvalidated) throw err;
    // 로그인한 적 없는 방문자의 401 은 «세션 만료» 가 아니다 — 갱신·홈 리다이렉트 없이 올린다.
    if (!authTokenStore.getState().accessToken) throw err;

    config._retried = true;
    if (!config.headers) config.headers = new AxiosHeaders();

    // 이 요청이 나간 뒤 다른 요청이 이미 재발급을 끝냈다 → 갱신 없이 새 토큰으로 재시도.
    // 그대로 refresh 를 또 부르면 방금 받은 refresh 쿠키를 한 번 더 회전시킨다.
    const current = authTokenStore.getState().accessToken;
    const sent = config.headers.get('Authorization');
    if (current && sent && sent !== `Bearer ${current}`) {
      return http.request(config); // 요청 인터셉터가 현재 토큰을 다시 넣는다
    }

    let newToken: string;
    try {
      newToken = await refreshOnce(http);
    } catch (refreshErr) {
      // 서버가 «거절» 한 것만 세션 만료다. 연결 실패·타임아웃·5xx 까지 로그아웃시키면
      // 네트워크가 한 번 흔들린 것으로 멀쩡한 세션을 잃는다.
      const s = (refreshErr as AxiosError).response?.status;
      if (s === 401 || s === 403) clearSessionAndReload();
      throw refreshErr;
    }

    config.headers.set('Authorization', `Bearer ${newToken}`);
    return http.request(config);
  }
);
